import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixAutomationsTemplatesV1AutomationTemplatesServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/automation-templates',
        destPath: '/api',
      },
      {
        srcPath: '/automation-templates',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_automations_automation-templates';

/** Creates an AutomationTemplate. */
export function createAutomationTemplate(
  payload: object
): RequestOptionsFactory<any> {
  function __createAutomationTemplate({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'automationTemplate.createdDate' },
          { path: 'automationTemplate.updatedDate' },
          { path: 'automationTemplate.automation.createdDate' },
          { path: 'automationTemplate.automation.updatedDate' },
          { path: 'automationTemplate.automation.draftUpdatedDate' },
          {
            path: 'automationTemplate.automation.autoArchivePolicy.archiveDate',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.automations.templates.v1.automation_template',
      method: 'POST' as any,
      methodFqn:
        'wix.automations.templates.v1.AutomationTemplatesService.CreateAutomationTemplate',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsTemplatesV1AutomationTemplatesServiceUrl({
        protoPath: '/v1/automation-templates',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'automationTemplate.createdDate' },
              { path: 'automationTemplate.updatedDate' },
              { path: 'automationTemplate.automation.createdDate' },
              { path: 'automationTemplate.automation.updatedDate' },
              { path: 'automationTemplate.automation.draftUpdatedDate' },
              {
                path: 'automationTemplate.automation.autoArchivePolicy.archiveDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createAutomationTemplate;
}

/** Retrieves an AutomationTemplate. */
export function getAutomationTemplate(
  payload: object
): RequestOptionsFactory<any> {
  function __getAutomationTemplate({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.automations.templates.v1.automation_template',
      method: 'GET' as any,
      methodFqn:
        'wix.automations.templates.v1.AutomationTemplatesService.GetAutomationTemplate',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsTemplatesV1AutomationTemplatesServiceUrl({
        protoPath: '/v1/automation-templates/{automationTemplateId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'automationTemplate.createdDate' },
              { path: 'automationTemplate.updatedDate' },
              { path: 'automationTemplate.automation.createdDate' },
              { path: 'automationTemplate.automation.updatedDate' },
              { path: 'automationTemplate.automation.draftUpdatedDate' },
              {
                path: 'automationTemplate.automation.autoArchivePolicy.archiveDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getAutomationTemplate;
}

/** Updates an AutomationTemplate. */
export function updateAutomationTemplate(
  payload: object
): RequestOptionsFactory<any> {
  function __updateAutomationTemplate({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'automationTemplate.createdDate' },
          { path: 'automationTemplate.updatedDate' },
          { path: 'automationTemplate.automation.createdDate' },
          { path: 'automationTemplate.automation.updatedDate' },
          { path: 'automationTemplate.automation.draftUpdatedDate' },
          {
            path: 'automationTemplate.automation.autoArchivePolicy.archiveDate',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.automations.templates.v1.automation_template',
      method: 'PATCH' as any,
      methodFqn:
        'wix.automations.templates.v1.AutomationTemplatesService.UpdateAutomationTemplate',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsTemplatesV1AutomationTemplatesServiceUrl({
        protoPath: '/v1/automation-templates/{automationTemplate.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'automationTemplate.createdDate' },
              { path: 'automationTemplate.updatedDate' },
              { path: 'automationTemplate.automation.createdDate' },
              { path: 'automationTemplate.automation.updatedDate' },
              { path: 'automationTemplate.automation.draftUpdatedDate' },
              {
                path: 'automationTemplate.automation.autoArchivePolicy.archiveDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateAutomationTemplate;
}

/** Deletes an AutomationTemplate. */
export function deleteAutomationTemplate(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteAutomationTemplate({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.automations.templates.v1.automation_template',
      method: 'DELETE' as any,
      methodFqn:
        'wix.automations.templates.v1.AutomationTemplatesService.DeleteAutomationTemplate',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsTemplatesV1AutomationTemplatesServiceUrl({
        protoPath: '/v1/automation-templates/{componentId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteAutomationTemplate;
}

/** Returns AutomationTemplates that are installed on the site and exposed to Wix users. */
export function resolveAutomationTemplates(
  payload: object
): RequestOptionsFactory<any> {
  function __resolveAutomationTemplates({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'query.sort.origin.latitude' },
          { path: 'query.sort.origin.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.automations.templates.v1.automation_template',
      method: 'POST' as any,
      methodFqn:
        'wix.automations.templates.v1.AutomationTemplatesService.ResolveAutomationTemplates',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsTemplatesV1AutomationTemplatesServiceUrl({
        protoPath: '/v1/automation-templates/resolve',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'automationTemplates.createdDate' },
              { path: 'automationTemplates.updatedDate' },
              { path: 'automationTemplates.automation.createdDate' },
              { path: 'automationTemplates.automation.updatedDate' },
              { path: 'automationTemplates.automation.draftUpdatedDate' },
              {
                path: 'automationTemplates.automation.autoArchivePolicy.archiveDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __resolveAutomationTemplates;
}

/** Allows an app to query its automation templates. */
export function queryAutomationTemplatesForApp(
  payload: object
): RequestOptionsFactory<any> {
  function __queryAutomationTemplatesForApp({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.automations.templates.v1.automation_template',
      method: 'POST' as any,
      methodFqn:
        'wix.automations.templates.v1.AutomationTemplatesService.QueryAutomationTemplatesForApp',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsTemplatesV1AutomationTemplatesServiceUrl({
        protoPath: '/v1/automation-templates/query-for-app',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'automationTemplates.createdDate' },
              { path: 'automationTemplates.updatedDate' },
              { path: 'automationTemplates.automation.createdDate' },
              { path: 'automationTemplates.automation.updatedDate' },
              { path: 'automationTemplates.automation.draftUpdatedDate' },
              {
                path: 'automationTemplates.automation.autoArchivePolicy.archiveDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryAutomationTemplatesForApp;
}
